/**
 * "Talk to Hero" voice widget — voice connection attempt.
 *
 * GitHub issue #555, root-cause investigation item #2: "Is the ElevenLabs/
 * Vapi connection ... WebSocket connecting? Check if the ngrok tunnel is
 * still alive." This module is the actual connection attempt: opens a
 * `WebSocket` against the configured voice server URL and resolves/rejects
 * based on `open`/`error`/close-before-open, with a hard timeout so a
 * hung/black-holed tunnel (exactly the "free ngrok tunnel expired"
 * scenario the ticket describes) can never leave the widget stuck on
 * "Connecting..." forever.
 *
 * Deliberately a thin, dependency-free wrapper around the browser's
 * native `WebSocket` rather than the VAPI Web SDK: no `@vapi-ai/web`
 * (or similar) package exists anywhere in this repo's `package.json`
 * today, and this run's rule #3 ("stay focused on this single issue")
 * argues against pulling in and wiring a brand-new third-party SDK
 * dependency as part of a P0 bugfix. The `VoiceConnection` interface
 * below is intentionally the minimal shape a real SDK-backed
 * implementation would also need to satisfy (`start()` / `stop()` /
 * `onStateChange`), so swapping this out for the real VAPI SDK later is a
 * drop-in replacement, not a rewrite of `useTalkToHeroWidget.ts`.
 */

export type VoiceConnectionFailureReason = 'ws_unreachable' | 'ws_error' | 'ws_timeout' | 'ws_closed_before_open';

export class VoiceConnectionError extends Error {
  constructor(
    message: string,
    public readonly reason: VoiceConnectionFailureReason
  ) {
    super(message);
    this.name = 'VoiceConnectionError';
  }
}

export interface ConnectVoiceCallOptions {
  wsUrl: string;
  /** Milliseconds to wait for the WebSocket to open before giving up. */
  timeoutMs?: number;
  /** Override for tests (defaults to the global `WebSocket`). */
  WebSocketImpl?: typeof WebSocket;
}

export const DEFAULT_VOICE_CONNECT_TIMEOUT_MS = 8000;

/** A live voice connection handle. `close()` ends the call/conversation. */
export interface VoiceConnectionHandle {
  close: () => void;
}

/**
 * Attempts to open the voice WebSocket connection. Resolves with a
 * {@link VoiceConnectionHandle} once the socket is open ("the call has
 * connected"); rejects with a {@link VoiceConnectionError} (never a bare
 * `Error`, so callers can always read `.reason`) if the socket errors,
 * closes before opening, or doesn't open within `timeoutMs`.
 */
export function connectVoiceCall(options: ConnectVoiceCallOptions): Promise<VoiceConnectionHandle> {
  const { wsUrl, timeoutMs = DEFAULT_VOICE_CONNECT_TIMEOUT_MS, WebSocketImpl } = options;
  const Ctor = WebSocketImpl ?? (typeof WebSocket !== 'undefined' ? WebSocket : undefined);

  return new Promise<VoiceConnectionHandle>((resolve, reject) => {
    if (!Ctor) {
      reject(new VoiceConnectionError('WebSocket is not available in this environment', 'ws_unreachable'));
      return;
    }

    let settled = false;
    let socket: WebSocket;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      try {
        socket?.close();
      } catch {
        // ignore close-during-teardown errors
      }
      reject(new VoiceConnectionError(`Voice service did not respond within ${timeoutMs}ms`, 'ws_timeout'));
    }, timeoutMs);

    try {
      socket = new Ctor(wsUrl);
    } catch (err) {
      clearTimeout(timer);
      reject(
        new VoiceConnectionError(
          err instanceof Error ? err.message : 'Failed to open voice WebSocket connection',
          'ws_unreachable'
        )
      );
      return;
    }

    socket.addEventListener('open', () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve({
        close: () => {
          try {
            socket.close();
          } catch {
            // ignore
          }
        },
      });
    });

    socket.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new VoiceConnectionError('Voice WebSocket connection failed', 'ws_error'));
    });

    socket.addEventListener('close', () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new VoiceConnectionError('Voice WebSocket closed before it opened', 'ws_closed_before_open'));
    });
  });
}
